import { useState } from 'react';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { StickyScrollFade } from '@/components/atoms/StickyScrollFade/StickyScrollFade';
import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { ComplexFilterBar } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { useScrollFade } from '@/hooks/useScrollFade';
import { SavedView } from '@/hooks/useTableFilters';
import { cn } from '@/lib/utils';

export interface FilterToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  filterDefinitions: FilterDefinition[];
  filters: FilterItem[];
  setFilter: (key: string, values: string[], exclude?: boolean) => void;
  removeFilter: (...keys: string[]) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  savedViews: SavedView[];
  activeView: SavedView | null;
  hasUnsavedChanges: boolean;
  onSaveView: (name: string) => string | null;
  onUpdateView: () => void;
  onDeleteView: () => void;
  onRenameView: (name: string) => string | null;
  onApplyView: (view: SavedView) => void;
  onClearActiveView: () => void;
  /** Indent the band past a drag-handle column left of the list, as on Rules. */
  inset?: boolean;
}

/**
 * Sticky search and filter band for a list page: search on the left, the filter menu on the
 * right, and the active filter pills with saved views underneath. Filter state comes from
 * `useTableFilters`; the page supplies its own filter definitions.
 */
export const FilterToolbar = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  filterDefinitions,
  filters,
  setFilter,
  removeFilter,
  resetFilters,
  activeFilterCount,
  savedViews,
  activeView,
  hasUnsavedChanges,
  onSaveView,
  onUpdateView,
  onDeleteView,
  onRenameView,
  onApplyView,
  onClearActiveView,
  inset = false,
}: FilterToolbarProps) => {
  const [openPillId, setOpenPillId] = useState<string | null>(null);
  const { scrollRef, showFade } = useScrollFade<HTMLDivElement>('top');

  const savedFilterItems = savedViews.map((v) => ({ id: v.id, name: v.name }));
  const activeSavedFilterView = activeView ? { id: activeView.id, name: activeView.name } : null;

  const applyViewById = (viewId: string) => {
    const view = savedViews.find((v) => v.id === viewId);
    if (view) {
      onApplyView(view);
    }
  };

  const handleClearFilters = () => {
    resetFilters();
    onClearActiveView();
  };

  return (
    <div
      ref={scrollRef}
      className={cn('sticky top-0 z-30 flex flex-col gap-4 bg-subtle pt-4 pb-3', inset && 'pl-6')}
    >
      <StickyScrollFade show={showFade} />
      <div className={cn('flex items-center justify-between', inset && 'pl-1')} role="search">
        <SearchBar
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <div className="flex items-center gap-2">
          <ComplexFilterCombobox
            filterDefinitions={filterDefinitions}
            filters={filters}
            setFilter={setFilter}
            removeFilter={removeFilter}
            activeFilterCount={activeFilterCount}
            savedFilters={savedFilterItems}
            onApplySavedFilter={(item) => applyViewById(item.id)}
            onOpenActivePill={setOpenPillId}
          />
        </div>
      </div>

      <ComplexFilterBar
        filters={buildFilterBadges(filterDefinitions, filters, { setFilter, removeFilter })}
        savedFiltersConfig={{
          savedFilters: savedFilterItems,
          activeSavedFilter: activeSavedFilterView,
          hasUnsavedChanges,
          onSave: onSaveView,
          onUpdate: onUpdateView,
          onDelete: onDeleteView,
          onRename: onRenameView,
          onSelectView: applyViewById,
        }}
        openPillId={openPillId}
        onOpenPillChange={setOpenPillId}
        onClearAll={handleClearFilters}
      />
    </div>
  );
};
